/**
 * Audio Notification Engine
 * 
 * Provides preloaded, debounced audio playback for notification sounds.
 * Features:
 * - Preloads audio files on mount for zero-latency playback
 * - 200ms debounce to prevent rapid-fire overlap
 * - AudioContext unlocking for browser autoplay policy compliance
 * - Global mute toggle support
 */

import type { NotificationSound } from '@shared/schema';

export type NotificationType = 
  | 'pulse'
  | 'chat'
  | 'mention'
  | 'email'
  | 'admin'
  | 'security'
  | 'activeCalls'
  | 'scheduledCalls';

export type ChatChannelType = 'project' | 'global' | 'topic' | 'direct';

export interface ChatAudioSettings {
  project?: number | null;
  global?: number | null;
  topic?: number | null;
  direct?: number | null;
}

export interface AudioSettings {
  audioNotificationsEnabled: boolean;
  soundPulseNotifications: NotificationSound | null;
  soundUnreadChats: NotificationSound | null;
  soundMentions: NotificationSound | null;
  soundEmailMatches: NotificationSound | null;
  soundAdminNotifications: NotificationSound | null;
  soundSecurityNotifications: NotificationSound | null;
  soundActiveCalls?: NotificationSound | null;
  soundScheduledCalls?: NotificationSound | null;
}

class AudioNotifier {
  private soundCache: Map<number, HTMLAudioElement> = new Map();
  private lastPlayTime: number = 0;
  private readonly debounceMs: number = 200;
  private audioContext: AudioContext | null = null;
  private globalEnabled: boolean = true;
  private soundIdMap: Map<NotificationType, number | null> = new Map();
  private chatSoundIdMap: Map<ChatChannelType, number | null> = new Map();
  private isInitialized: boolean = false;
  private isUnlocked: boolean = false;
  private unlockListenerAttached: boolean = false;
  private lastSettingsHash: string | null = null;
  // A chime that could not play (background tab / autoplay block) is kept
  // here and replayed once when the tab becomes visible again.
  private pendingChime: { soundId: number; timestamp: number } | null = null;
  private readonly pendingChimeMaxAgeMs: number = 5 * 60 * 1000;
  private visibilityListenerAttached: boolean = false;

  constructor() {
    // Attach one-time unlock listener on any user interaction
    this.attachUnlockListener();
    this.attachVisibilityListener();
  }

  /**
   * When the tab becomes visible again, replay a chime that was blocked
   * while backgrounded (once, if recent) — so a blocked "pling" is caught
   * up instead of silently lost.
   */
  private attachVisibilityListener(): void {
    if (typeof document === 'undefined' || this.visibilityListenerAttached) return;
    this.visibilityListenerAttached = true;

    document.addEventListener('visibilitychange', () => {
      if (document.hidden || !this.pendingChime) return;
      const { soundId, timestamp } = this.pendingChime;
      this.pendingChime = null;
      if (Date.now() - timestamp > this.pendingChimeMaxAgeMs) return;
      // Bypass the 200ms debounce — this is a deliberate one-shot catch-up,
      // not a rapid-fire duplicate.
      this.lastPlayTime = 0;
      this.playSound(soundId);
    });
  }

  /**
   * Attach listener to unlock AudioContext on first user interaction
   */
  private attachUnlockListener(): void {
    if (typeof window === 'undefined' || this.unlockListenerAttached) return;
    
    const unlockHandler = () => {
      this.unlockAudioContext();
      // Remove listeners after first unlock
      document.removeEventListener('click', unlockHandler);
      document.removeEventListener('touchstart', unlockHandler);
      document.removeEventListener('keydown', unlockHandler);
      // Listener set is now fully detached — allow a future re-arm.
      this.unlockListenerAttached = false;
    };
    
    document.addEventListener('click', unlockHandler, { once: true, passive: true });
    document.addEventListener('touchstart', unlockHandler, { once: true, passive: true });
    document.addEventListener('keydown', unlockHandler, { once: true, passive: true });
    this.unlockListenerAttached = true;
  }

  /**
   * Preload user's selected notification sounds
   * Called on app mount after fetching user settings
   */
  async preloadUserSounds(settings: AudioSettings): Promise<void> {
    const currentHash = JSON.stringify({
      enabled: settings.audioNotificationsEnabled,
      pulse: settings.soundPulseNotifications?.id,
      chat: settings.soundUnreadChats?.id,
      mention: settings.soundMentions?.id,
      email: settings.soundEmailMatches?.id,
      admin: settings.soundAdminNotifications?.id,
      security: settings.soundSecurityNotifications?.id,
      activeCalls: settings.soundActiveCalls?.id,
      scheduledCalls: settings.soundScheduledCalls?.id,
    });

    if (this.lastSettingsHash === currentHash) {
      return;
    }

    this.lastSettingsHash = currentHash;
    this.globalEnabled = settings.audioNotificationsEnabled;
    
    const soundMappings: [NotificationType, NotificationSound | null][] = [
      ['pulse', settings.soundPulseNotifications],
      ['chat', settings.soundUnreadChats],
      ['mention', settings.soundMentions],
      ['email', settings.soundEmailMatches],
      ['admin', settings.soundAdminNotifications],
      ['security', settings.soundSecurityNotifications],
      ['activeCalls', settings.soundActiveCalls ?? null],
      ['scheduledCalls', settings.soundScheduledCalls ?? null],
    ];
    
    console.log('[AudioNotifier] Preloading user sounds...');
    this.soundCache.clear();
    this.soundIdMap.clear();
    
    const preloadPromises: Promise<void>[] = [];
    
    for (const [type, sound] of soundMappings) {
      if (sound?.directUrl) {
        this.soundIdMap.set(type, sound.id);
        
        if (!this.soundCache.has(sound.id)) {
          preloadPromises.push(this.preloadSound(sound));
        }
      } else {
        this.soundIdMap.set(type, null);
      }
    }
    
    await Promise.allSettled(preloadPromises);
    this.isInitialized = true;
    console.log(`[AudioNotifier] Preloaded ${this.soundCache.size} sounds`);
  }

  /**
   * Preload a single sound into the cache
   */
  private async preloadSound(sound: NotificationSound): Promise<void> {
    if (!sound.directUrl) return;
    
    try {
      const audio = new Audio();
      audio.preload = 'auto';
      audio.src = sound.directUrl;
      
      // Wait for the audio to be loaded
      await new Promise<void>((resolve, reject) => {
        audio.oncanplaythrough = () => resolve();
        audio.onerror = () => reject(new Error(`Failed to load audio: ${sound.name}`));
        audio.load();
        
        // Timeout after 5 seconds
        setTimeout(() => resolve(), 5000);
      });
      
      this.soundCache.set(sound.id, audio);
      console.log(`[AudioNotifier] Preloaded: ${sound.name} (ID: ${sound.id})`);
    } catch (error) {
      console.warn(`[AudioNotifier] Failed to preload ${sound.name}:`, error);
    }
  }

  /**
   * Unlock AudioContext for browsers requiring user interaction
   * Should be called on user interaction (e.g., speaker toggle click)
   */
  unlockAudioContext(): void {
    if (this.isUnlocked) return;
    
    try {
      if (!this.audioContext) {
        this.audioContext = new AudioContext();
      }
      
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume().then(() => {
          this.isUnlocked = true;
          console.log('[AudioNotifier] AudioContext unlocked');
          setTimeout(() => {
            this.audioContext?.suspend();
            console.log('[AudioNotifier] AudioContext suspended after unlock (releasing hardware)');
          }, 100);
        });
      } else {
        this.isUnlocked = true;
        console.log('[AudioNotifier] AudioContext already running');
        this.audioContext.suspend();
      }
    } catch (error) {
      console.warn('[AudioNotifier] Failed to unlock AudioContext:', error);
    }
  }

  /**
   * Play a notification sound by type with 200ms debounce
   */
  playByType(type: NotificationType): void {
    const soundId = this.soundIdMap.get(type);
    this.playSound(soundId ?? null);
  }

  /**
   * Play a notification sound by ID with 200ms debounce
   */
  playSound(soundId: number | null): void {
    // Check global enabled
    if (!this.globalEnabled) {
      return;
    }
    
    // Check sound exists
    if (!soundId) {
      return;
    }
    
    // Debounce: prevent rapid-fire overlap
    const now = Date.now();
    if (now - this.lastPlayTime < this.debounceMs) {
      console.log('[AudioNotifier] Debounced - skipping sound');
      return;
    }
    this.lastPlayTime = now;
    
    // Get cached audio
    const audio = this.soundCache.get(soundId);
    if (!audio) {
      console.warn(`[AudioNotifier] Sound ${soundId} not in cache`);
      return;
    }
    
    // Reset and play
    try {
      audio.currentTime = 0;
      audio.play().catch((error) => {
        console.log('[AudioNotifier] Play blocked:', error.message);
        this.handlePlayFailure(soundId);
      });
    } catch (error) {
      console.warn('[AudioNotifier] Error playing sound:', error);
      this.handlePlayFailure(soundId);
    }
  }

  /**
   * A blocked play is never just swallowed: remember it for a one-shot
   * replay when the tab becomes visible, and re-arm the unlock listener so
   * the next user gesture restores audio (browsers can revoke the unlock,
   * e.g. Brave after backgrounding).
   */
  private handlePlayFailure(soundId: number): void {
    this.pendingChime = { soundId, timestamp: Date.now() };
    this.isUnlocked = false;
    // attachUnlockListener is a no-op while a listener set is still armed
    // (unlockListenerAttached only resets inside the unlock handler), so
    // repeated blocked plays can never stack multiple handlers.
    this.attachUnlockListener();
  }

  /**
   * Set global audio notifications enabled state
   */
  setGlobalEnabled(enabled: boolean): void {
    this.globalEnabled = enabled;
    console.log(`[AudioNotifier] Global audio ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Get global enabled state
   */
  isGlobalEnabled(): boolean {
    return this.globalEnabled;
  }

  /**
   * Check if the notifier has been initialized with sounds
   */
  isReady(): boolean {
    return this.isInitialized;
  }

  /**
   * Update a single sound setting without full reload
   */
  updateSoundSetting(type: NotificationType, sound: NotificationSound | null): void {
    this.soundIdMap.set(type, sound?.id ?? null);
    
    if (sound?.directUrl && !this.soundCache.has(sound.id)) {
      this.preloadSound(sound);
    }
  }

  /**
   * Update chat audio settings for channel-specific notifications
   */
  updateChatAudioSettings(settings: ChatAudioSettings, availableSounds: NotificationSound[]): void {
    this.chatSoundIdMap.clear();
    
    const channelTypes: ChatChannelType[] = ['project', 'global', 'topic', 'direct'];
    
    for (const channelType of channelTypes) {
      const soundId = settings[channelType] ?? null;
      this.chatSoundIdMap.set(channelType, soundId);
      
      // Preload the sound if not already cached
      if (soundId) {
        const sound = availableSounds.find(s => s.id === soundId);
        if (sound?.directUrl && !this.soundCache.has(soundId)) {
          this.preloadSound(sound);
        }
      }
    }
    
    console.log('[AudioNotifier] Updated chat audio settings:', Object.fromEntries(this.chatSoundIdMap));
  }

  /**
   * Play a notification sound for a specific chat channel type
   * Falls back to generic 'chat' sound if no channel-specific sound is set
   */
  playByChatChannelType(channelType: ChatChannelType): void {
    // First try channel-specific sound
    const channelSoundId = this.chatSoundIdMap.get(channelType);
    if (channelSoundId) {
      this.playSound(channelSoundId);
      return;
    }
    
    // Fall back to generic chat sound
    this.playByType('chat');
  }

  /**
   * Get sound ID for a specific chat channel type
   */
  getChatSoundId(channelType: ChatChannelType): number | null {
    return this.chatSoundIdMap.get(channelType) ?? null;
  }
}

// Singleton instance
export const audioNotifier = new AudioNotifier();
